import { Pill, PillVariant } from '@aus-platform/design-system';
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { BaseResourceSpec } from 'shared/api';

type SingleLinePillListProps = {
  items: BaseResourceSpec[];
};

export const SingleLinePillList: React.FC<SingleLinePillListProps> = ({
  items,
}) => {
  // Refs.
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRowRef = useRef<HTMLDivElement>(null);

  // States.
  const [visibleCount, setVisibleCount] = useState(items.length);

  // Effects.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measureRow = measureRowRef.current;

    if (!row || !measureRow) {
      return;
    }

    const getPillWidth = (pill?: HTMLElement) =>
      pill?.getBoundingClientRect().width ?? 0;

    const calculateVisibleCount = () => {
      // clientWidth is an integer; pill widths are subpixel floats. Leave 1px so
      // the last pill does not clip under overflow: hidden.
      const availableWidth = row.clientWidth - 1;
      const measuredPills = Array.from(measureRow.children) as HTMLElement[];
      const overflowPillWidth = getPillWidth(
        measuredPills[measuredPills.length - 1],
      );
      const gap = parseFloat(getComputedStyle(measureRow).columnGap) || 0;

      let consumedWidth = 0;
      let fittingCount = 0;

      for (let index = 0; index < items.length; index++) {
        const pillWidth = getPillWidth(measuredPills[index]);
        const widthWithGap = index === 0 ? pillWidth : gap + pillWidth;
        const isLastItem = index === items.length - 1;
        
        const reservedWidth = isLastItem ? 0 : gap + overflowPillWidth;

        if (consumedWidth + widthWithGap + reservedWidth > availableWidth) {
          break;
        }

        consumedWidth += widthWithGap;
        fittingCount += 1;
      }

      setVisibleCount(Math.max(fittingCount, 1));
    };

    calculateVisibleCount();

    const resizeObserver = new ResizeObserver(calculateVisibleCount);
    resizeObserver.observe(row);
    resizeObserver.observe(measureRow);

    return () => resizeObserver.disconnect();
  }, [items]);

  // Variables.
  const hiddenCount = items.length - visibleCount;

  return (
    <div className="single-line-pill-list">
      <div className="single-line-pill-list__row" ref={rowRef}>
        {items.slice(0, visibleCount).map((item) => (
          <Pill key={item.id} variant={PillVariant.Info}>
            {item.name}
          </Pill>
        ))}

        {hiddenCount > 0 && (
          <Pill variant={PillVariant.Info}>+{hiddenCount}</Pill>
        )}
      </div>

      <div className="single-line-pill-list__measure" aria-hidden>
        <div className="single-line-pill-list__measure-row" ref={measureRowRef}>
          {items.map((item) => (
            <Pill key={item.id} variant={PillVariant.Info}>
              {item.name}
            </Pill>
          ))}

          <Pill variant={PillVariant.Info}>+{items.length}</Pill>
        </div>
      </div>
    </div>
  );
};
